import { useMemo, useState, type ReactNode } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@cyphex/ui';
import { searchGifs, STICKERS } from '../../data/stickers';
import type { GifItem, StickerItem } from '../../types';
import { PickerPopover } from './PickerPopover';

interface GifPickerProps {
  trigger: ReactNode;
  onSelect: (gif: GifItem) => void;
}

/** GIF picker over a local catalogue (see data/stickers.ts for the swap note). */
export function GifPicker({ trigger, onSelect }: GifPickerProps) {
  const [query, setQuery] = useState('');
  const results = useMemo(() => searchGifs(query), [query]);

  return (
    <PickerPopover trigger={trigger} label="Pick a GIF">
      <Input
        variant="search"
        size="sm"
        hideLabel
        label="Search GIFs"
        leadingIcon={Search}
        placeholder="Search GIFs"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="scrollbar-quiet mt-2 grid max-h-64 grid-cols-2 gap-1.5 overflow-y-auto pr-1">
        {results.map((gif) => (
          <button
            key={gif.id}
            type="button"
            onClick={() => onSelect(gif)}
            className="overflow-hidden rounded-md border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img
              src={gif.previewUrl}
              alt={gif.title}
              width={gif.width}
              height={gif.height}
              loading="lazy"
              decoding="async"
              className="aspect-[4/3] w-full object-cover"
            />
          </button>
        ))}
      </div>
    </PickerPopover>
  );
}

interface StickerPickerProps {
  trigger: ReactNode;
  onSelect: (sticker: StickerItem) => void;
}

/** Sticker picker — emoji-composed packs, zero image payload. */
export function StickerPicker({ trigger, onSelect }: StickerPickerProps) {
  const packs = useMemo(() => [...new Set(STICKERS.map((sticker) => sticker.pack))], []);

  return (
    <PickerPopover trigger={trigger} label="Pick a sticker">
      <div className="scrollbar-quiet max-h-64 overflow-y-auto pr-1">
        {packs.map((pack) => (
          <section key={pack} className="mb-2">
            <h3 className="mb-1 px-1 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
              {pack}
            </h3>
            <div className="grid grid-cols-3 gap-1.5">
              {STICKERS.filter((sticker) => sticker.pack === pack).map((sticker) => (
                <button
                  key={sticker.id}
                  type="button"
                  onClick={() => onSelect(sticker)}
                  className="flex flex-col items-center gap-1 rounded-lg border border-border p-2 transition-colors duration-fast hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="text-2xl" aria-hidden>
                    {sticker.emoji}
                  </span>
                  <span className="text-sm text-muted-foreground">{sticker.label}</span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </PickerPopover>
  );
}
